export type EntityScreenMode = 'view' | 'edit' | 'create' | null;
export type EntityDetailMode = Exclude<EntityScreenMode, 'create' | null>;

export interface ParsedEntityScreenHash {
    section: 'person' | 'groups' | 'classes' | 'localities' | 'admin' | 'other' | 'meta';
    entityType?: string;
    mode: EntityDetailMode;
    entityId: string;
}

function normalizeBaseHash(baseHash: string): string {
    const trimmed = (baseHash ?? '').trim().replace(/^#/, '').replace(/\/+$/, '');
    return trimmed;
}

function normalizeHash(hash: string): string {
    const trimmed = (hash ?? '').trim();
    return trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
}

export function buildEntityScreenHash(
    baseHash: string,
    mode: EntityDetailMode,
    entityId: string,
): string {
    const normalizedBase = normalizeBaseHash(baseHash);
    const normalizedId = encodeURIComponent((entityId ?? '').trim());
    if (!normalizedBase || !normalizedId) {
        return normalizedBase ? `#${normalizedBase}` : '#';
    }

    return `#${normalizedBase}/${mode}/${normalizedId}`;
}

export function canonicalEntityScreenHash(parsed: ParsedEntityScreenHash): string {
    let baseHash: string;
    switch (parsed.section) {
        case 'groups':
            baseHash = '#groups';
            break;
        case 'classes':
            baseHash = '#classes';
            break;
        case 'person':
            baseHash = `#Person/${(parsed.entityType ?? '').toLowerCase()}`;
            break;
        case 'localities':
            baseHash = `#localities/${(parsed.entityType ?? '').toLowerCase()}`;
            break;
        case 'admin':
            baseHash = `#admin/${parsed.entityType ?? ''}`;
            break;
        case 'other':
            baseHash = `#other/${parsed.entityType ?? ''}`;
            break;
        case 'meta':
            baseHash = `#meta/${(parsed.entityType ?? '').toLowerCase()}`;
            break;
    }
    return buildEntityScreenHash(baseHash, parsed.mode, parsed.entityId);
}

/** True when the path uses legacy `/legacy/` or transitional `/newui/` segments (should redirect to canonical). */
export function entityScreenHashHasExplicitVariantPrefix(hashWithoutSearch: string): boolean {
    const parts = hashWithoutSearch.split('/').filter(Boolean);
    if (parts.length < 3) return false;

    const section = (parts[0] ?? '').toLowerCase();
    if (section === 'groups' || section === 'classes') {
        if (parts.length >= 4) {
            const routeVariant = (parts[1] ?? '').toLowerCase();
            return routeVariant === 'legacy' || routeVariant === 'newui';
        }
        return false;
    }

    if (parts.length >= 5) {
        const routeVariant = (parts[2] ?? '').toLowerCase();
        return routeVariant === 'legacy' || routeVariant === 'newui';
    }

    return false;
}

export function parseEntityScreenHash(hash: string): ParsedEntityScreenHash | null {
    const normalizedHash = normalizeHash(hash);
    if (!normalizedHash) return null;

    const [hashWithoutSearch] = normalizedHash.split('?');
    const parts = hashWithoutSearch.split('/').filter(Boolean);
    if (parts.length < 3) return null;

    const section = (parts[0] ?? '').toLowerCase();
    if (!['person', 'groups', 'classes', 'localities', 'admin', 'other', 'meta'].includes(section)) {
        return null;
    }

    if ((section === 'admin') && (parts[1] ?? '').toLowerCase() === 'pluginfunctions') {
        return null;
    }

    if (section === 'groups' || section === 'classes') {
        if (parts.length === 3) {
            const mode = (parts[1] ?? '').toLowerCase();
            const entityId = parts[2] ?? '';
            if ((mode === 'view' || mode === 'edit') && entityId) {
                return {
                    section,
                    mode,
                    entityId,
                };
            }
        }

        if (parts.length === 4) {
            const routeVariant = (parts[1] ?? '').toLowerCase();
            const mode = (parts[2] ?? '').toLowerCase();
            const entityId = parts[3] ?? '';
            if ((routeVariant === 'newui' || routeVariant === 'legacy') && (mode === 'view' || mode === 'edit') && entityId) {
                return {
                    section,
                    mode,
                    entityId,
                };
            }
        }

        return null;
    }

    if (parts.length === 4) {
        const entityType = parts[1] ?? '';
        const mode = (parts[2] ?? '').toLowerCase();
        const entityId = parts[3] ?? '';
        if (entityType && (mode === 'view' || mode === 'edit') && entityId) {
            return {
                section: section as ParsedEntityScreenHash['section'],
                entityType,
                mode,
                entityId,
            };
        }
    }

    if (parts.length === 5) {
        const entityType = parts[1] ?? '';
        const routeVariant = (parts[2] ?? '').toLowerCase();
        const mode = (parts[3] ?? '').toLowerCase();
        const entityId = parts[4] ?? '';
        if (entityType && (routeVariant === 'newui' || routeVariant === 'legacy') && (mode === 'view' || mode === 'edit') && entityId) {
            return {
                section: section as ParsedEntityScreenHash['section'],
                entityType,
                mode,
                entityId,
            };
        }
    }

    return null;
}
